import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import { TargetSelect } from '@code-yellow/spider';
import { PlanningTicket } from '../../store/planning/PlanningTicket';
import { User, UserStore } from '../../store/User';
import { api } from '../../store/Base';
import RemarkupContent from './shared/RemarkupContent';
import { OutlineIcon } from './shared/OutlineIcon';

const C = {
    bg: '#ffffff', border: '#e0e3e8', borderStrong: '#c5cad2',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', accentLight: '#e8f3fb',
    success: '#21ba45', warning: '#f0a721', danger: '#db2828',
    sidebarBg: '#f8f9fa', inputBg: '#f8f9fa',
};

const STATUS_OPTIONS = [
    { text: 'To Do', value: 'todo', color: '#9ca3af' },
    { text: 'In Progress', value: 'in_progress', color: '#2185d0' },
    { text: 'Review', value: 'review', color: '#fbbf24' },
    { text: 'Done', value: 'done', color: '#4ade80' },
    { text: 'Blocked', value: 'blocked', color: '#ef4444' },
];

const Panel = styled.div<{ visible: boolean }>`
    position: absolute; top: 0; right: 0; bottom: 0; width: 360px;
    background: ${C.bg}; box-shadow: -4px 0 20px rgba(0,0,0,0.12);
    z-index: 50; display: flex; flex-direction: column;
    transition: transform 0.25s ease;
    transform: ${props => props.visible ? 'translateX(0)' : 'translateX(100%)'};
    border-left: 1px solid ${C.border};
`;
const PanelHeader = styled.div`
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid ${C.border}; background: ${C.sidebarBg};
    flex-shrink: 0;
`;
const PanelTitle = styled.h3` font-size: 14px; font-weight: 600; color: ${C.textPrimary}; margin: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const CloseBtn = styled.button`
    background: none; border: none; font-size: 20px; color: ${C.textMuted}; cursor: pointer; padding: 4px 8px;
    &:hover { color: ${C.textPrimary}; }
`;
const PanelContent = styled.div` flex: 1; overflow-y: auto; padding: 16px; `;
const Section = styled.div` margin-bottom: 20px; `;
const SectionLabel = styled.label`
    display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    color: ${C.textSecondary}; margin-bottom: 6px;
`;
const TextInput = styled.input`
    width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border};
    border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;
const TextArea = styled.textarea`
    width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border};
    border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit; resize: vertical; min-height: 60px;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;
const DateInput = styled.input`
    width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border};
    border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;
const NumberInput = styled.input`
    width: 100%; padding: 8px 10px; background: ${C.inputBg}; border: 1px solid ${C.border};
    border-radius: 4px; color: ${C.textPrimary}; font-size: 13px; font-family: inherit;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;
const StatusButtonGroup = styled.div` display: flex; gap: 4px; flex-wrap: wrap; `;
const StatusButton = styled.button<{ active: boolean; color: string }>`
    padding: 5px 12px; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;
    border: 1px solid ${props => props.active ? props.color : C.border};
    background: ${props => props.active ? props.color : C.bg};
    color: ${props => props.active ? '#fff' : C.textSecondary};
    &:hover { border-color: ${props => props.color}; }
`;
const DateRow = styled.div` display: flex; gap: 8px; `;
const DateCol = styled.div` flex: 1; `;
const DeleteBtn = styled.button`
    width: 100%; padding: 8px; border: 1px solid ${C.danger}; border-radius: 4px;
    background: #fff; color: ${C.danger}; cursor: pointer; font-size: 13px; font-weight: 500;
    &:hover { background: ${C.danger}; color: #fff; }
`;
const UnassignBtn = styled.button`
    width: 100%; padding: 8px; border: 1px solid ${C.warning}; border-radius: 4px;
    background: #fff; color: ${C.warning}; cursor: pointer; font-size: 13px; font-weight: 500;
    &:hover { background: ${C.warning}; color: #fff; }
`;
const SaveIndicator = styled.span<{ visible: boolean }>`
    font-size: 11px; color: ${C.success}; opacity: ${props => props.visible ? 1 : 0}; transition: opacity 0.3s;
`;

// ── [T54255] Description: rendered view + edit toggle ──────────────────────

const DescSectionHead = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
`;

const DescSectionLabel = styled(SectionLabel)`
    margin-bottom: 0;
    flex: 1;
`;

const DescToggleBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: 1px solid ${C.border};
    border-radius: 4px;
    color: ${C.textSecondary};
    font-size: 11px;
    padding: 2px 8px;
    cursor: pointer;
    &:hover { background: ${C.accentLight}; border-color: ${C.accent}; color: ${C.accent}; }
`;

const DescRendered = styled.div`
    border: 1px solid ${C.border};
    border-radius: 4px;
    background: #fafbfc;
    padding: 8px 10px;
    max-height: 260px;
    overflow-y: auto;
`;

const DescEmptyNote = styled.div`
    color: ${C.textMuted};
    font-size: 12.5px;
    font-style: italic;
    border: 1px dashed ${C.border};
    border-radius: 4px;
    padding: 8px 10px;
    background: #fafbfc;
`;

// ── [T54255] Description popup editor — the expand button next to the
// description label opens a full-screen editor with a lot more room to write
// than the 360px panel. Editing happens on a local draft; Save PATCHes the
// raw description (the backend pushes it to Phabricator and re-renders
// description_html server-side), Cancel/Escape discard the draft.
//
// NOTE: the popup is rendered OUTSIDE the <Panel> element — the panel's own
// transform would otherwise become the containing block for this
// fixed-position backdrop.

const DescExpandBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid ${C.border};
    border-radius: 4px;
    color: ${C.textSecondary};
    cursor: pointer;
    padding: 2px 6px;
    &:hover { background: ${C.accentLight}; border-color: ${C.accent}; color: ${C.accent}; }
`;

const DescPopupBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(23, 26, 33, 0.4);
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
`;

const DescPopup = styled.div`
    background: ${C.bg};
    border: 1px solid ${C.border};
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    width: 760px;
    max-width: 100%;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const DescPopupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid ${C.border};
    background: ${C.sidebarBg};
    flex-shrink: 0;
`;

const DescPopupTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: ${C.textPrimary};
    margin: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DescPopupClose = styled.button`
    background: none;
    border: none;
    font-size: 18px;
    color: ${C.textMuted};
    cursor: pointer;
    padding: 2px 8px;
    line-height: 1;
    &:hover { color: ${C.textPrimary}; }
`;

const DescPopupBody = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 20px;
    flex: 1;
    min-height: 0;
`;

const DescPopupEditor = styled.textarea`
    flex: 1;
    width: 100%;
    min-height: 45vh;
    padding: 10px 12px;
    background: ${C.inputBg};
    border: 1px solid ${C.border};
    border-radius: 4px;
    color: ${C.textPrimary};
    font-size: 14px;
    line-height: 1.6;
    font-family: inherit;
    resize: none;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;

const DescPopupFooter = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid ${C.border};
    flex-shrink: 0;
`;

const DescPopupHint = styled.span`
    flex: 1;
    font-size: 11px;
    color: ${C.textMuted};
`;

const DescPopupBtn = styled.button<{ primary?: boolean }>`
    padding: 6px 14px;
    border: 1px solid ${(p: any) => (p.primary ? C.accent : C.borderStrong)};
    border-radius: 4px;
    background: ${(p: any) => (p.primary ? C.accent : '#fff')};
    color: ${(p: any) => (p.primary ? '#fff' : C.textSecondary)};
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: default; }
`;

interface Props {
    ticket: PlanningTicket | null;
    visible: boolean;
    users: User[];
    onClose: () => void;
    onChanged: () => void;
}

@observer
export default class TicketEditPanel extends React.Component<Props> {
    @observable saving = false;
    @observable justSaved = false;
    // [T54255] Description shows rendered by default; the toggle swaps to
    // the raw-text editor (same debounced save as before).
    @observable descEditing = false;
    // [T54255] Full-screen description editor popup (open from the expand
    // button next to the description label). Draft is local; Save PATCHes it.
    @observable descPopupOpen = false;
    @observable descPopupDraft = '';
    // Debounced save: buffer text edits and PATCH once typing stops, instead of
    // spamming the API (and the Phabricator sync behind it) on every keystroke.
    @observable pendingFields: Record<string, any> | null = null;
    pendingTicketId: number | null = null;
    saveTimer: any = null;
    debounceTimer: any = null;
    static SAVE_DEBOUNCE_MS = 800;
    userStore = new UserStore({ limit: false, relations: ['team'] });

    toUserOption = (u: User) => {
        const aname = (u as any).fullName || ((u as any).firstName || '') + ' ' + ((u as any).lastName || '').trim() || (u as any).email;
        return { text: aname, value: u.id };
    };

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.flushPending();
    }

    componentDidUpdate(prevProps: Props) {
        // Flush buffered edits when the panel closes or switches to another
        // ticket, so nothing typed is lost and saves land on the right ticket.
        if (!this.props.visible && prevProps.visible) {
            this.flushPending();
        }
        if (this.pendingFields && this.pendingTicketId !== null
            && this.pendingTicketId !== (this.props.ticket?.id ?? null)) {
            this.flushPending();
        }
    }

    /** Save any buffered text edits immediately (used on close/switch/unmount/Enter). */
    @action flushPending = () => {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        const fields = this.pendingFields ? { ...this.pendingFields } : null;
        const ticketId = this.pendingTicketId;
        this.pendingFields = null;
        this.pendingTicketId = null;
        if (!fields || !ticketId) return;
        // Never send a blank title — the backend rejects it (title is required).
        if (fields.title !== undefined && !String(fields.title).trim()) {
            delete fields.title;
        }
        if (Object.keys(fields).length) {
            this.patch(fields, ticketId);
        }
    };

    /** Buffer a debounced PATCH: replaces any earlier buffered edit of the same field. */
    @action queueSave = (fields: Record<string, any>) => {
        const id = this.props.ticket?.id;
        if (!id) return;
        this.pendingTicketId = id;
        this.pendingFields = { ...(this.pendingFields || {}), ...fields };
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.flushPending();
        }, TicketEditPanel.SAVE_DEBOUNCE_MS);
    };

    @action handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.props.visible) {
            e.preventDefault();
            // The popup editor has Escape priority: close the popup, not the
            // panel. (The editor textarea stops propagation and closes it
            // itself when it has focus — this covers every other focus spot.)
            if (this.descPopupOpen) {
                this.closeDescPopup();
                return;
            }
            this.props.onClose();
        }
    };

    @action patch = async (fields: Record<string, any>, ticketId?: number) => {
        const id = ticketId ?? this.props.ticket?.id;
        if (!id) return;
        this.saving = true;
        try {
            await api.patch('planning_ticket/' + id + '/', fields);
            this.justSaved = true;
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => { this.justSaved = false; }, 1500);
            this.props.onChanged();
        } catch (e: any) {
            console.error('TicketEditPanel patch error:', e);
        } finally {
            this.saving = false;
        }
    };

    @action handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        (this.props.ticket as any).title = val;
        // Buffer the edit; the PATCH fires once typing stops (see queueSave).
        // Blank titles are not buffered — the backend rejects them (required).
        if (val.trim()) {
            this.queueSave({ title: val });
        }
    };

    @action handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.flushPending();
        }
    };
    @action handleStatusChange = (status: string) => {
        (this.props.ticket as any).status = status;
        this.patch({ status });
    };
    @action afterAssigneeChange = () => {
        // TargetSelect already updated ticket.assignee on the model via setValue
        const val = (this.props.ticket as any).assignee?.id ?? null;
        this.patch({ assignee: val });
    };
    @action handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value) || 0;
        (this.props.ticket as any).estimatedHours = val;
        this.patch({ estimated_hours: val });
    };
    @action handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value || null;
        (this.props.ticket as any).scheduledStart = val;
        this.patch({ scheduled_start: val });
    };
    @action handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value || null;
        (this.props.ticket as any).scheduledEnd = val;
        this.patch({ scheduled_end: val });
    };
    @action handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        (this.props.ticket as any).description = val;
        this.queueSave({ description: val });
    };

    // ── [T54255] Description view/edit toggle ──

    @action toggleDescEditing = () => {
        this.descEditing = !this.descEditing;
        if (!this.descEditing) {
            // Leaving edit mode: flush any buffered raw edit so nothing
            // typed is lost (queueSave holds real edits — flushPending sends them).
            this.flushPending();
        }
    };

    // ── [T54255] Description popup editor ──

    @action openDescPopup = () => {
        // Flush any buffered inline edit first, so the popup starts from the
        // real state and a pending debounce cannot overwrite the popup save.
        this.flushPending();
        this.descPopupDraft = (this.props.ticket as any)?.description || '';
        this.descPopupOpen = true;
    };

    @action closeDescPopup = () => {
        this.descPopupOpen = false;
        this.descPopupDraft = '';
    };

    @action handleDescPopupChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.descPopupDraft = e.target.value;
    };

    /** PATCH the raw description; the backend pushes it to Phabricator and
     * re-renders description_html server-side. */
    @action saveDescPopup = async () => {
        const tk: any = this.props.ticket;
        if (!tk?.id) return;
        await this.patch({ description: this.descPopupDraft }, tk.id);
        tk.description = this.descPopupDraft;
        this.closeDescPopup();
    };

    @action handleDescPopupKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Ctrl/Cmd+Enter saves; Escape closes (stops propagation so the panel's
        // document-level Escape handler does not also close the panel).
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            this.saveDescPopup();
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeDescPopup();
        }
    };
    @action handleDelete = async () => {
        if (!this.props.ticket?.id) return;
        if (!window.confirm('Delete this ticket?')) return;
        try {
            await api.delete('planning_ticket/' + this.props.ticket.id + '/');
            this.props.onClose();
            this.props.onChanged();
        } catch (e: any) {
            console.error('Delete ticket error:', e);
        }
    };
    @action handleUnassign = async () => {
        if (!this.props.ticket?.id) return;
        const t = this.props.ticket as any;
        t.assignee = null;
        t.scheduledStart = null;
        t.scheduledEnd = null;
        this.saving = true;
        try {
            await api.patch('planning_ticket/' + this.props.ticket.id + '/', {
                assignee: null,
                scheduled_start: null,
                scheduled_end: null,
            });
            this.justSaved = true;
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => { this.justSaved = false; }, 1500);
            this.props.onChanged();
        } catch (e: any) {
            console.error('Unassign ticket error:', e);
        } finally {
            this.saving = false;
        }
    };

    /** [T54255] Panel description body: raw editor while editing, else the
     * rendered description — falling back to the raw text when no rendered
     * HTML exists yet (ticket never synced), or the empty note. Named
     * helper instead of a nested ternary (lint). */
    renderPanelDescription(tAny: any) {
        if (this.descEditing) {
            return (
                <TextArea
                    data-test-ticket-panel-description
                    value={tAny.description || ''}
                    onChange={this.handleDescriptionChange}
                />
            );
        }
        if (tAny.descriptionHtml && String(tAny.descriptionHtml).trim()) {
            return (
                <DescRendered data-test-ticket-panel-desc-rendered>
                    <RemarkupContent html={tAny.descriptionHtml} />
                </DescRendered>
            );
        }
        if (tAny.description) {
            // Raw text exists but no rendered HTML yet (ticket never synced).
            return (
                <DescRendered data-test-ticket-panel-desc-raw>
                    {tAny.description}
                </DescRendered>
            );
        }
        return <DescEmptyNote>No description.</DescEmptyNote>;
    }

    render() {
        const { ticket, visible, onClose } = this.props;
        if (!ticket) return null;

        const tAny = ticket as any;
        const assigneeId = tAny.assignee?.id ?? tAny.assignee;
        const fmtDate = (d: any) => {
            if (!d) return '';
            const m = moment(d);
            return m.isValid() ? m.format('YYYY-MM-DD') : '';
        };

        return (
            <>
                <Panel visible={visible}>
                <PanelHeader>
                    <PanelTitle>
                        {tAny.phabricatorTaskNumber ? 'T' + tAny.phabricatorTaskNumber + ' ' : ''}
                        {tAny.title || ('#' + ticket.id)}
                    </PanelTitle>
                    <SaveIndicator visible={this.justSaved}>✓ Saved</SaveIndicator>
                    <SaveIndicator visible={this.saving || !!this.pendingFields} data-test-ticket-panel-saving>Saving…</SaveIndicator>
                    <CloseBtn onClick={onClose} data-test-ticket-panel-close>×</CloseBtn>
                </PanelHeader>
                <PanelContent>
                    <Section>
                        <SectionLabel>Title</SectionLabel>
                        <TextInput
                            data-test-ticket-panel-title
                            value={tAny.title || ''}
                            onChange={this.handleTitleChange}
                            onKeyDown={this.handleTitleKeyDown}
                        />
                    </Section>

                    <Section>
                        <SectionLabel>Status</SectionLabel>
                        <StatusButtonGroup>
                            {STATUS_OPTIONS.map(opt => (
                                <StatusButton
                                    key={opt.value}
                                    active={tAny.status === opt.value}
                                    color={opt.color}
                                    onClick={() => this.handleStatusChange(opt.value)}
                                    data-test-ticket-panel-status={opt.value}
                                >
                                    {opt.text}
                                </StatusButton>
                                ))}
                        </StatusButtonGroup>
                    </Section>

                    <Section>
                        <SectionLabel>Assignee</SectionLabel>
                        <TargetSelect
                            target={ticket}
                            name="assignee"
                            remote={true}
                            clearable={true}
                            store={this.userStore}
                            toOption={this.toUserOption}
                            afterChange={this.afterAssigneeChange}
                            data-test-ticket-panel-assignee
                        />
                    </Section>

                    <Section>
                        <SectionLabel>Estimated Hours</SectionLabel>
                        <NumberInput
                            type="number"
                            data-test-ticket-panel-hours
                            value={tAny.estimatedHours ?? 0}
                            onChange={this.handleHoursChange}
                        />
                    </Section>

                    <Section>
                        <SectionLabel>Schedule</SectionLabel>
                        <DateRow>
                            <DateCol>
                                <DateInput
                                    type="date"
                                    data-test-ticket-panel-start
                                    value={fmtDate(tAny.scheduledStart)}
                                    onChange={this.handleStartChange}
                                />
                            </DateCol>
                            <DateCol>
                                <DateInput
                                    type="date"
                                    data-test-ticket-panel-end
                                    value={fmtDate(tAny.scheduledEnd)}
                                    onChange={this.handleEndChange}
                                />
                            </DateCol>
                        </DateRow>
                    </Section>

                    <Section>
                        <DescSectionHead>
                            <DescSectionLabel>Description</DescSectionLabel>
                            <DescToggleBtn
                                onClick={this.toggleDescEditing}
                                data-test-ticket-panel-desc-toggle
                            >
                                {this.descEditing ? 'View rendered' : 'Edit raw'}
                            </DescToggleBtn>
                            <DescExpandBtn
                                type="button"
                                onClick={this.openDescPopup}
                                title="Open description in a large editor"
                                data-test-ticket-panel-desc-expand
                            >
                                <OutlineIcon name="expand" size={12} />
                            </DescExpandBtn>
                        </DescSectionHead>
                        {this.renderPanelDescription(tAny)}
                    </Section>

                    {(assigneeId || tAny.scheduledStart) && (
                        <Section>
                            <UnassignBtn onClick={this.handleUnassign} data-test-ticket-panel-unassign>
                                Unassign &amp; Clear Schedule
                            </UnassignBtn>
                        </Section>
                    )}
                    <Section>
                        <DeleteBtn onClick={this.handleDelete} data-test-ticket-panel-delete>
                            Delete Ticket
                        </DeleteBtn>
                    </Section>
                </PanelContent>
            </Panel>
                {/* [T54255] Description popup editor — sibling of the Panel so
                    the panel's transform does not capture the fixed backdrop. */}
                {this.descPopupOpen && this.renderDescPopup(tAny)}
            </>
        );
    }

    /** [T54255] Full-screen description editor opened from the expand button
     * next to the description: one large textarea with Save/Cancel — far more
     * room to write than the small inline editor in the 360px panel. */
    renderDescPopup(tAny: any) {
        return (
            <DescPopupBackdrop
                data-test-ticket-panel-desc-popup
                onClick={this.closeDescPopup}
            >
                <DescPopup onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <DescPopupHeader>
                        <DescPopupTitle data-test-ticket-panel-desc-popup-title>
                            {tAny.phabricatorTaskNumber ? 'T' + tAny.phabricatorTaskNumber + ' — ' : ''}
                            Edit description: {tAny.title || ('#' + this.props.ticket?.id)}
                        </DescPopupTitle>
                        <DescPopupClose
                            onClick={this.closeDescPopup}
                            data-test-ticket-panel-desc-popup-close
                        >
                            ×
                        </DescPopupClose>
                    </DescPopupHeader>
                    <DescPopupBody>
                        <DescPopupEditor
                            autoFocus
                            value={this.descPopupDraft}
                            onChange={this.handleDescPopupChange}
                            onKeyDown={this.handleDescPopupKeyDown}
                            data-test-ticket-panel-desc-popup-editor
                        />
                    </DescPopupBody>
                    <DescPopupFooter>
                        <DescPopupHint>Ctrl+Enter to save · Esc to close without saving</DescPopupHint>
                        <DescPopupBtn
                            type="button"
                            onClick={this.closeDescPopup}
                            data-test-ticket-panel-desc-popup-cancel
                        >
                            Cancel
                        </DescPopupBtn>
                        <DescPopupBtn
                            type="button"
                            primary
                            onClick={this.saveDescPopup}
                            disabled={this.saving}
                            data-test-ticket-panel-desc-popup-save
                        >
                            {this.saving ? 'Saving…' : 'Save'}
                        </DescPopupBtn>
                    </DescPopupFooter>
                </DescPopup>
            </DescPopupBackdrop>
        );
    }
}
